import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  getNotices,
  createNotice,
  updateNotice,
  reorderNotices,
  deleteNotice,
  getNoticeFileUrl,
  type ImportantNotice,
} from '../../features/notices';
import { Card, DashboardPageHeader } from '../../shared/ui';
import { getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';
import NoticeModal from '../../components/dashboard/NoticeModal';

const NoticesPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [notices, setNotices] = useState<ImportantNotice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState<ImportantNotice | null>(null);
  const [saving, setSaving] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const canView = !!profile && can(profile, 'notices', 'view');
  const canEdit = !!profile && can(profile, 'notices', 'edit');
  const canDelete = !!profile && can(profile, 'notices', 'delete');

  const loadNotices = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const list = await getNotices();
      setNotices(list);
    } catch (err) {
      logErrorInDev('Failed to load notices', err);
      setError(getUserFriendlyErrorMessage(err, 'Failed to load notices.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canView) void loadNotices();
  }, [canView]);

  const handleCreate = async (formData: FormData) => {
    setSaving(true);
    setError(null);
    try {
      await createNotice(formData);
      setShowAddModal(false);
      await loadNotices();
      showToast('Notice added.');
    } catch (err) {
      logErrorInDev('Failed to create notice', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (
    id: string,
    data: { title: string; descriptionHtml: string | null }
  ) => {
    setSaving(true);
    setError(null);
    try {
      await updateNotice(id, data);
      setEditingNotice(null);
      await loadNotices();
      showToast('Notice updated.');
    } catch (err) {
      logErrorInDev('Failed to update notice', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this notice? It will no longer appear on the home page.')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteNotice(id);
      await loadNotices();
      showToast('Notice removed.');
    } catch (err) {
      logErrorInDev('Failed to delete notice', err);
      setError(getUserFriendlyErrorMessage(err, 'Failed to delete notice.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === dropIndex) return;
    const reordered = [...notices];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, removed);
    setNotices(reordered);
    try {
      await reorderNotices(reordered.map((n) => n.id));
      showToast('Notice order saved.');
    } catch (err) {
      console.error(err);
      setError('Failed to save order');
      await loadNotices();
    }
  };

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> You do not have permission to view important notices.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <DashboardPageHeader
          title="Important Notices"
          description="Manage what appears in the carousel on the home page."
          icon="bi bi-megaphone"
          actions={
            canEdit ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
                disabled={saving}
              >
                <i className="bi bi-plus-circle me-2" />
                Add Notice
              </button>
            ) : null
          }
        />

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        <Card title="Home page carousel" icon="bi bi-images">
          <div className="notices-page-intro mb-4">
            <p className="text-muted mb-0 small">
              <i className="bi bi-arrow-down-up me-2 text-primary" />
              Drag rows to change the order on the home page. New notices appear at the end.
            </p>
          </div>
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted mb-0">Loading notices...</p>
            </div>
          ) : notices.length === 0 ? (
            <div className="text-center py-5 px-3 notices-page-empty">
              <i className="bi bi-megaphone display-4 text-muted mb-3 d-block" style={{ opacity: 0.5 }} />
              <p className="text-muted mb-2">No notices yet.</p>
              <p className="small text-muted mb-4">Add your first notice to show on the home page carousel.</p>
              {canEdit && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
                disabled={saving}
              >
                <i className="bi bi-plus-circle me-2" />
                Add your first notice
              </button>
              )}
            </div>
          ) : (
            <div className="notices-list">
              {notices.map((notice, index) => (
                <div
                  key={notice.id}
                  className={`notices-list-item ${dragOverIndex === index ? 'notices-list-item--drag-over' : ''}`}
                  draggable={canEdit}
                  onDragStart={canEdit ? (e) => handleDragStart(e, index) : undefined}
                  onDragOver={canEdit ? (e) => handleDragOver(e, index) : undefined}
                  onDragLeave={canEdit ? handleDragLeave : undefined}
                  onDrop={canEdit ? (e) => handleDrop(e, index) : undefined}
                >
                  <div className={`notices-list-item__handle ${!canEdit ? 'opacity-50' : ''}`} title={canEdit ? 'Drag to reorder' : 'View only'}>
                    <i className="bi bi-grip-vertical" />
                  </div>
                  <div className="notices-list-item__thumb">
                    <img
                      src={getNoticeFileUrl(notice.id)}
                      alt=""
                      onError={(e) => {
                        const el = e.target as HTMLImageElement;
                        el.style.display = 'none';
                        const next = el.nextElementSibling as HTMLElement;
                        if (next) next.style.display = 'flex';
                      }}
                    />
                    <span className="notices-list-item__thumb-placeholder" style={{ display: 'none' }}>
                      <i className="bi bi-image" />
                    </span>
                  </div>
                  <div className="notices-list-item__body">
                    <div className="notices-list-item__title fw-bold mb-1">
                      {notice.title || 'Important Notice'}
                    </div>
                    <div className="notices-list-item__description">
                      {notice.descriptionHtml ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: notice.descriptionHtml.slice(0, 120) + (notice.descriptionHtml.length > 120 ? '…' : ''),
                          }}
                          className="rich-text-preview"
                        />
                      ) : notice.description ? (
                        notice.description.slice(0, 120) + (notice.description.length > 120 ? '…' : '')
                      ) : (
                        <em className="text-muted">No description</em>
                      )}
                    </div>
                    <span className="notices-list-item__order badge bg-light text-dark border mt-2">
                      Position {index + 1} of {notices.length}
                    </span>
                  </div>
                  <div className="notices-list-item__actions">
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={saving}
                        onClick={() => setEditingNotice(notice)}
                        title="Edit description"
                      >
                        <i className="bi bi-pencil me-1" />
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={saving}
                        onClick={() => handleDelete(notice.id)}
                        title="Remove from carousel"
                      >
                        <i className="bi bi-trash me-1" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <NoticeModal
          show={showAddModal}
          mode="add"
          onHide={() => setShowAddModal(false)}
          onSubmitAdd={handleCreate}
          saving={saving}
        />
        <NoticeModal
          show={!!editingNotice}
          mode="edit"
          notice={editingNotice!}
          onHide={() => setEditingNotice(null)}
          onSubmitEdit={handleUpdate}
          saving={saving}
        />
      </div>
    </div>
  );
};

export default NoticesPage;
